import React, { useState, useEffect } from "react";
import Image from "next/image";
import axios from "axios";
import ShareModal from "@/src/components/content/ShareModal";
import CompanyLikeButton from "../content/CompanyLikeButton";
import { getCompanyLiked } from "@/src/app/hooks/companies.hooks";

const CompanyHeader = ({ ticker, session, company, frc_covered, onRealTimePriceUpdate }) => {
  const defaultImageUrl = "/images/default-image.png";
  const [realTimePrice, setRealTimePrice] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(defaultImageUrl);
  const [companyLiked, setCompanyLiked] = useState(false);

  const formatCompanyNameForUrl = (name) => {
    return encodeURIComponent(
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric characters with hyphens
        .replace(/-+$/, "") // Remove trailing hyphens
    );
  };

  const [companyDetails, setCompanyDetails] = useState({
    companyName: company ? company.name : "",
    exchangeShortName: "",
    mktCap: "",
    sharesOutstanding: "",
    logoUrl: "",
    changes: 0,
  });
  const [changeInValue, setChangeInValue] = useState("");
  const [percentageChange, setPercentageChange] = useState("");
  const [sharesOutstanding, setSharesOutstanding] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const index = company.tickers.findIndex((t) => t === ticker);
  const exchange = company.exchange[index];
  const companyNameForUrl = formatCompanyNameForUrl(company.name);
  const url = `https://www.researchfrc.com/stocks/${ticker}/${companyNameForUrl}`;
  const title = `${company.name} - ${exchange} - ${ticker}`;

  // Fetch real-time price without caching as the value is highly dynamic
  useEffect(() => {
    const fetchPrice = async () => {
      setIsLoading(true); // Start loading for both price and details

      try {
        const response = await axios.post(
          "/api/company/stock",
          { ticker: ticker },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data && response.data.length > 0) {
          const data = response.data[0];
          const price = Number(data.price).toFixed(2);
          setRealTimePrice(price);
          onRealTimePriceUpdate(price); // Call callback with new price
          setChangeInValue(data.change);
          setPercentageChange(data.changesPercentage);
          setSharesOutstanding(data.sharesOutstanding);
        } else {
          setRealTimePrice("N/A");
          onRealTimePriceUpdate(null); // Call callback with null
          setChangeInValue("N/A");
          setPercentageChange("N/A");
          setSharesOutstanding("N/A");
        }
      } catch (error) {
        console.error("There has been a problem with your fetch operation");
        setRealTimePrice("N/A");
        onRealTimePriceUpdate(null); // Call callback with null
        setChangeInValue("N/A");
        setPercentageChange("N/A");
        setSharesOutstanding("N/A");
      }
      setIsLoading(false); // End loading after both fetches
    };

    fetchPrice();
  }, [ticker, onRealTimePriceUpdate]);

  // Fetch company details

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      setIsLoading(true); // Start loading for both price and details

      try {
        const response = await axios.post(
          "/api/company/details",
          { ticker: ticker },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.status === 200) {
          throw new Error("Network response was not ok");
        }
        const data = await response.data;
        setCompanyLogo(data[0].image || defaultImageUrl);
        setCompanyDetails({
          companyName: data[0].companyName,
          exchangeShortName: data[0].exchangeShortName,
          mktCap: data[0].mktCap,
          changes: data[0].changes, // Get the changes from the response
          changeValue: data[0].change,
        });
        setIsLoading(false); // End loading after both fetches
      } catch (error) {
        console.error("There has been a problem with your fetch operation");
      }
    };

    fetchCompanyDetails();
  }, [ticker]);

  //Fetch Likes
  useEffect(() => {
    const fetchCompanyLiked = async () => {
      if (ticker && session?.user.apiToken) {
        const response = await getCompanyLiked(ticker, session.user.apiToken);
        // Update your state based on postLiked response

        if (response.status === 200) {
          setCompanyLiked(response?.data?.data.liked);
        }
      }
    };
    fetchCompanyLiked();
  }, [ticker, session?.user.apiToken]);

  // Determine the text color based on positive or negative change
  const changeClass =
    companyDetails.changes < 0 ? "text-red-600 " : "text-green-600";

  // Utility function to format numbers
  const formatNumber = (num) => {
    if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
    if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
    if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
    return num.toString();
  };

  return (
    <>
      <div className="flex static border shadow-md h-250 justify-between items-center px-4 rounded-lg bg-E6E6E6  sm:h-250">
        <div
          className="mr-2"
          style={{ display: "flex", FlexDirection: "column" }}
        >
          {companyLogo && (
            <Image
              className="m-4"
              unoptimized
              src={companyLogo} // Fallback to a default image if the URL is not available
              alt={`Corporate logo of ${
                companyDetails.companyName || "the company"
              }, displayed on their profile page`}
              width={64}
              height={64} // or your desired height
              onError={() => setCompanyLogo(defaultImageUrl)}
            />
          )}
          <div>
            <h1 className="text-lg sm:text-2xl font-bold">
              {companyDetails.companyName
                ? companyDetails.companyName
                : company.name}{" "}
            </h1>
            <p className="text-xs sm:text-base text-gray-600">
              {companyDetails.exchangeShortName
                ? companyDetails.exchangeShortName
                : exchange}{" "}
              - {ticker}
            </p>
            <div className="text-sm text-gray-800 mt-1">
              {companyDetails.mktCap && (
                <p>
                  {" "}
                  <strong> Market Cap:</strong>{" "}
                  {formatNumber(companyDetails.mktCap)}
                </p>
              )}
              {companyDetails.sharesOutstanding && (
                <p>
                  {" "}
                  <strong> Shares OS: </strong>
                  {formatNumber(sharesOutstanding) || "N/A"}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Price and Change Information */}
        {realTimePrice && realTimePrice !== "N/A" && (
          <div className="text-right flex flex-col justify-between">
            <p className="text-xs sm:text-sm">Current Price</p>
            <p className="text-lg sm:text-2xl mt-1 font-bold">
              {realTimePrice ? `$${realTimePrice}` : "N/A"}
            </p>
            <div className={`${changeClass} text-sm font-bold mt-1`}>
              {realTimePrice !== 0 &&
              isFinite(companyDetails.changes / realTimePrice) &&
              companyDetails.changes &&
              Number(realTimePrice) !== 0
                ? `${Number(changeInValue).toFixed(2)} (${Number(
                    percentageChange
                  ).toFixed(2)}%)`
                : "N/A"}
            </div>
            {/* FRC Covered Chip */}
            {frc_covered && (
              <div className="mt-2">
                <p className="inline-block px-2 py-1 text-E6E6E6 text-xs bg-1B51CA rounded-md touch-manipulation">
                  Covered by FRC
                </p>
              </div>
            )}
          </div>
        )}

        {/* New div for ShareModal and LikeButton, aligned to the right */}
        <div className=" absolute mt-44 flex gap-3 ml-10">
          <ShareModal size="sm" url={url} title={title} />
          <CompanyLikeButton
            isLiked={companyLiked}
            companyId=""
            ticker={ticker}
            token={session?.user.apiToken}
          />
        </div>
      </div>
    </>
  );
};

export default CompanyHeader;
